import { formatDate } from "@aclymatepackages/formatters";

import { isInClosedYear } from "./reviewLineGates";
import {
  REVIEW_STATES,
  UTILITY_SUBCATEGORIES,
  isCurrencyUnit
} from "../../../helpers/smartUploadLineRequirements";
import {
  ATTENTION_REASONS,
  attentionReasonsFor
} from "../../../helpers/smartUploadCsvAttention";

export const CSV_PAGE_SIZE = 50;

export const SHIPPING_MODE_OPTIONS = [
  { value: "road", name: "Road" },
  { value: "air", name: "Air" },
  { value: "sea", name: "Sea" },
  { value: "rail", name: "Rail" }
];

const ISO_DATE_PREFIX = /^\d{4}-\d{2}-\d{2}/;

// Lines come back from the API as JSON, so a Firestore Timestamp arrives as
// { _seconds } and an ISO date keeps only its calendar day (a UTC midnight
// would otherwise show the day before in US time zones).
export const csvDateValue = (value) => {
  if (!value) {
    return null;
  }
  if (value.toDate) {
    return value.toDate();
  }
  if (typeof value === "string" && ISO_DATE_PREFIX.test(value)) {
    return value.slice(0, 10);
  }
  const seconds = value._seconds ?? value.seconds;
  return seconds === undefined ? value : new Date(seconds * 1000);
};

export const formatCsvDate = (value) => {
  const date = csvDateValue(value);
  return date ? formatDate(date) : "—";
};

export const normalizeCsvLine = (line) => ({
  ...line,
  period_start_final: csvDateValue(line.period_start_final),
  period_end_final: csvDateValue(line.period_end_final)
});

export const csvLineKeyFor = ({ rowId, id, line_id: lineId }) =>
  `${rowId}/${id || lineId}`;

const formatNumber = (value) =>
  Number(value).toLocaleString("en-US", { maximumFractionDigits: 2 });

export const formatCsvQuantity = ({
  quantity_final: quantity,
  unit_final: unit
}) => {
  if (quantity === null || quantity === undefined || quantity === "") {
    return "—";
  }
  const amount = isCurrencyUnit(unit)
    ? Number(quantity).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    : formatNumber(quantity);
  return unit ? `${amount} ${unit}` : amount;
};

export const csvDescriptionFor = ({ legacy_row: legacyRow = {} }) =>
  legacyRow.description || legacyRow.vendor || "—";

export const csvRouteFor = ({ legacy_row: legacyRow = {} }) =>
  `${legacyRow.senderPostalCode || "—"} → ${
    legacyRow.recipientPostalCode || legacyRow.recipientCountry || "—"
  }`;

export const shippingModeName = (mode) =>
  SHIPPING_MODE_OPTIONS.find(({ value }) => value === mode)?.name ||
  mode ||
  "—";

// bom_products_csv (ticket DlAT2n1ITtjB6BA9ntuJ): product/supplier formatters,
// parallel to csvDescriptionFor/csvVendorNameFor above.
export const csvProductNameFor = (line) =>
  line.product_name_final || line.product_name_ai || "—";

export const csvSupplierNameFor = (line) => line.supplier_name || "—";

export const csvFactoryCodeFor = (line) => line.factory_code || "—";

export const csvCategoryFor = (line) =>
  line.category_final || line.category_ai || "—";

// utilities_csv/office_csv (ticket #412): usage-reading formatters, parallel
// to the product/supplier formatters above.
export const csvUsageTypeFor = (line) => line.usage_type_label || "—";

export const formatCsvTons = (tons) =>
  tons === null || tons === undefined ? "—" : `${formatNumber(tons)} tCO2e`;

const PURCHASED_WATER_SUBCATEGORY = "purchased-water";
const REFRIGERANTS_SUBCATEGORY = "refrigerants";

// office_csv's one extra column varies by subcategory: a purchased-water line
// carries the wastewater split used in calcOfficeUsageEmissions, a
// refrigerants line carries which gas it is -- every other subcategory (steam,
// chill-water) has no extra input to show.
export const csvOfficeSubcategoryExtraFor = ({
  subcategory_final: subcategory,
  usage_inputs: usageInputs
}) => {
  if (subcategory === PURCHASED_WATER_SUBCATEGORY) {
    const wastewaterPercentage = usageInputs?.wastewaterPercentage;
    return wastewaterPercentage === undefined || wastewaterPercentage === null
      ? "—"
      : `${wastewaterPercentage}%`;
  }
  if (subcategory === REFRIGERANTS_SUBCATEGORY) {
    return usageInputs?.refrigerantType || "—";
  }
  return "—";
};

export const csvVendorNameFor = (line) =>
  line.vendor_name_resolved ||
  line.vendor_name_final ||
  line.vendor_match_ai?.name ||
  line.vendor_name_ai ||
  line.plaid_enrichment?.vendor?.name ||
  "—";

const isNewVendor = (line) =>
  !!line.force_new_vendor ||
  (!line.vendor_relationship_id_final &&
    !!line.vendor_name_final &&
    !line.vendor_match_accepted) ||
  !line.vendor_match_ai?.vendorId;

// The badge next to a line's vendor: how the name was (or will be) resolved.
export const vendorBadgeFor = (line) => {
  if (line.vendor_relationship_id_final || line.vendor_match_accepted) {
    return { label: "Matched", color: "secondary" };
  }
  if (isNewVendor(line)) {
    return { label: "New vendor — will be created", color: "default" };
  }
  if ((line.vendor_match_ai.match_source || "").startsWith("plaid_entity")) {
    return { label: "Matched via bank data", color: "secondary" };
  }
  if (line.vendor_match_ai.match_source === "fuzzy") {
    return { label: "Possible match", color: "warning" };
  }
  return { label: "Matched", color: "secondary" };
};

export const ATTENTION_REASON_COPY = {
  [ATTENTION_REASONS.possibleDuplicate]:
    "Looks like this was already added — Skip or Keep.",
  [ATTENTION_REASONS.vendorMatchUnconfirmed]:
    "Check the vendor we matched this to.",
  [ATTENTION_REASONS.noVendor]: "This row has no vendor — add one.",
  [ATTENTION_REASONS.vendorFactorMissing]:
    "We couldn't find an emission factor for this vendor — pick a different vendor.",
  [ATTENTION_REASONS.vendorLookupFailed]:
    "We couldn't look up this vendor — pick or enter it.",
  [ATTENTION_REASONS.bomNewSupplier]:
    "This supplier isn't on file yet — pick an existing one or confirm creating it.",
  [ATTENTION_REASONS.bomSupplierLookupFailed]:
    "We couldn't look up this supplier — pick or enter one."
};

const isUtilitySpend = (line) =>
  UTILITY_SUBCATEGORIES.has(line.vendor_match_ai?.emissionCategory) ||
  UTILITY_SUBCATEGORIES.has(line.subcategory_final);

// A match with no added date (a bank feed, accounting or utility-import
// record) carries only the activity's own date, which still tells the
// reviewer which record it is -- but must be worded as that record's date,
// not when it was added.
export const duplicateAddedOn = (duplicateOf) => {
  const value = duplicateOf?.added_at || duplicateOf?.date;
  return value ? { value, isConfirmed: Boolean(duplicateOf.added_at) } : null;
};

// findOfficeUsageDuplicate.js's raw-quantity leg (ticket #412) stamps
// matched_on: "raw_quantity" on top of the generic existing_transaction
// match_type -- match_type alone can't tell this apart from
// transactions_csv/shipping_transactions's own existing_transaction match
// (commitMigratedCsvLine.js's hash-based dedup uses the same match_type for
// an unrelated case).
const isRawQuantityMatch = (duplicateOf) =>
  duplicateOf?.matched_on === "raw_quantity";

export const duplicateNote = ({ duplicate_of: duplicateOf }) => {
  if (isRawQuantityMatch(duplicateOf)) {
    return "Same bill reading as an existing record.";
  }
  const addedOn = duplicateAddedOn(duplicateOf);
  if (!addedOn) {
    return null;
  }
  const date = formatCsvDate(addedOn.value);
  return addedOn.isConfirmed
    ? `Possibly already added on ${date}.`
    : `Possibly already added (dated ${date}).`;
};

export const csvLineNotes = (line, closedYears) =>
  [
    ...attentionReasonsFor(line).map((reason) => ATTENTION_REASON_COPY[reason]),
    duplicateNote(line),
    isInClosedYear(line, closedYears) &&
      "Closed year — it'll be recorded but excluded from totals.",
    isUtilitySpend(line) &&
      "Utility spend is recorded but excluded — utilities are tracked from bills.",
    line.review_state === REVIEW_STATES.error &&
      (line.commit_error?.message || "This line couldn't be recorded yet.")
  ].filter(Boolean);

const CSV_STATUS_LABELS = {
  committed: "Recorded",
  committing: "Recording…",
  rejected: "Rejected",
  error: "Couldn't record",
  manual_fallback: "Moved to manual entry",
  duplicate_skipped: "Skipped"
};

export const csvStatusFor = (line) => {
  if (line.review_state === REVIEW_STATES.pendingReview) {
    return attentionReasonsFor(line).length
      ? { label: "Needs attention", color: "warning" }
      : { label: "Ready", color: "secondary" };
  }
  return {
    label: CSV_STATUS_LABELS[line.review_state] || line.review_state,
    color: line.review_state === REVIEW_STATES.error ? "error" : "default"
  };
};
